import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { dialogTypes, snackbarTypes } from '@/constants/alertTypes';
import { NEW_VIEW } from '@/constants/dashboardConstants';
import alertsUtil from '@/util/alertsUtil';
import useProject from '@API/project/useProject';
import useProjectViews from '@API/project/useProjectViews';
import { DashboardView } from '@experiment-management-shared/types';
import { fromDashboardToTemplate } from '@experiment-management-shared/utils/EMView';
import { useDashboardApiContext } from '@projects/components/EMDashboardPage/useDashboardAPIContext';
import { getNewNameCount } from '@shared';
import { useViewSelectUpsertConfig } from '@shared/components/ViewSelect';
import {
  VIEW_SELECT_ACTION_MODE,
  ViewSelectUpsertActionFn,
  ViewSelectUpsertFormData
} from '@shared/components/ViewSelect/hooks/useViewSelectUpsertConfig';
import { FormViewModalProps } from '@shared/components/ViewSelect/modals/FormViewModal';
import useChangeCurrentView from './useChangeCurrentView';
import useSetDefaultView from './useSetDefaultView';
import useProjectViewDetails from '@API/project/useProjectViewDetails';

export const EMPTY_TEMPLATE_ID = 'empty_template';

const useDashboardViewActions = ({
  dashboard
}: {
  dashboard: DashboardView;
}) => {
  const dispatch = useDispatch();
  const { data: project = {} } = useProject();
  const { projectId, projectName } = project as {
    projectId?: string;
    projectName?: string;
  };
  const { data: templates = [] } = useProjectViews();
  const templatesNames = useMemo(() => {
    return templates.map(v => v.templateName);
  }, [templates]);

  // Just setting properties for the modal (title/button-label)
  const {
    newViewDefaultName,
    modalOpened,
    editId,
    mode,
    modalCopy,
    onEdit,
    onCreate,
    onDuplicate,
    onClose
  } = useViewSelectUpsertConfig();

  const {
    isSavingTemplate,
    saveTemplateOnServer,
    saveTemplateOnServerDebounced
  } = useDashboardApiContext();
  const onSetAsDefaultView = useSetDefaultView();
  const { onSelectView, changeCurrentView } = useChangeCurrentView();

  const templateToEdit = useMemo(() => {
    return templates.find(view => view.templateId === editId);
  }, [editId, templates]);

  const templateIdToFetch = templateToEdit?.templateId;

  const { data: templateDetails } = useProjectViewDetails(
    { templateId: templateIdToFetch, viewProjectId: templateToEdit?.projectId },
    { enabled: !!templateIdToFetch }
  );

  const viewData = useMemo(() => {
    if (!project) return;

    if (
      mode === VIEW_SELECT_ACTION_MODE.EDIT_MODE &&
      dashboard.isPersonalView &&
      dashboard.templateId === editId
    ) {
      return fromDashboardToTemplate({ dashboard, project });
    }

    if (
      mode === VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE &&
      (dashboard.isPersonalView || dashboard.templateId === editId)
    ) {
      return fromDashboardToTemplate({ dashboard, project });
    }

    return templateDetails;
  }, [dashboard, editId, project, templateDetails, mode]);

  const clonedViewDefaultName = dashboard.isPersonalView
    ? newViewDefaultName
    : viewData?.templateName ?? newViewDefaultName;
  const clonesCount = getNewNameCount(
    templatesNames,
    `${clonedViewDefaultName} (Copy`
  );
  const notRenamedNewViewCount = getNewNameCount(
    templatesNames,
    `${newViewDefaultName}`,
    /\((\d+)\)$/
  );

  const onEditTemplate = useCallback(
    (id: string) => {
      onEdit(id);
      // double-check, flush won't wait until the save is done to proceed with the next action
      saveTemplateOnServerDebounced.flush();
    },
    [onEdit, saveTemplateOnServerDebounced]
  );

  const onCreateNewTemplate = useCallback(() => {
    onCreate();
    saveTemplateOnServerDebounced.flush();
  }, [onCreate, saveTemplateOnServerDebounced]);

  const onDuplicateTemplate = useCallback(
    (id: string) => {
      onDuplicate(id);

      // on new-view after duplicate = discarded so no need to save any pending changes
      if (editId !== NEW_VIEW) {
        // double-check, flush won't wait until the save is done to proceed with the next action
        saveTemplateOnServerDebounced.flush();
      }
    },
    [editId, onDuplicate, saveTemplateOnServerDebounced]
  );

  const onEditView: ViewSelectUpsertActionFn = useCallback(
    (templateName, isDefault) => {
      if (!viewData?.templateId) return;

      if (isDefault) {
        onSetAsDefaultView({
          viewId: viewData.templateId
        });
      }

      saveTemplateOnServer(
        { view: { ...viewData, templateName } },
        { onSuccess: onClose }
      );

      saveTemplateOnServerDebounced.flush();
    },
    [
      onClose,
      onSetAsDefaultView,
      saveTemplateOnServer,
      saveTemplateOnServerDebounced,
      viewData
    ]
  );

  const onDuplicateView: ViewSelectUpsertActionFn = useCallback(
    (templateName, isDefault) => {
      if (!viewData || !projectId || !projectName) return;

      // on new-view before duplicate ->
      //   canceling saving since no need to save any pending changes (on create new one BE will discard it)
      // on a saved view ->
      //   it will auto-save it in the auto-save logic (flush after redirect) no need to flush here
      if (!viewData.templateId || viewData.templateId === NEW_VIEW) {
        saveTemplateOnServerDebounced.cancel();
      }

      saveTemplateOnServer(
        {
          view: {
            ...viewData,
            templateName,
            templateId: null,
            createdFromTemplateId: viewData.templateId,
            current: true,
            currentProjectId: projectId,
            projectId,
            source: projectName,
            unsavedView: false,
            v3: {
              ...viewData.v3,
              autoGenerate: false,
              isPersonalView: false
            }
          }
        },
        {
          onSuccess: ({ template_id }) => {
            if (isDefault) {
              onSetAsDefaultView({
                viewId: template_id
              });
            }

            changeCurrentView(template_id);

            dispatch(
              alertsUtil.openSnackbarDialog(
                snackbarTypes.SUCCESS_API_RESPONSE,
                `Your view was successfully duplicated`
              )
            );

            onClose();
          },
          onError: e => {
            /// @ts-expect-error fix later
            const { msg } = e.response.data;

            dispatch(
              alertsUtil.openErrorDialog(
                dialogTypes.CATCH_ERROR_SAVE_TEMPLATE,
                `There was an error duplicating your dashboard view: ${msg}`
              )
            );
          }
        }
      );
    },
    [
      changeCurrentView,
      dispatch,
      onClose,
      onSetAsDefaultView,
      projectId,
      projectName,
      saveTemplateOnServer,
      saveTemplateOnServerDebounced,
      viewData
    ]
  );

  const onCreateNewView: ViewSelectUpsertActionFn = useCallback(
    (templateName, isDefault) => {
      if (!projectId || !projectName) return;

      saveTemplateOnServerDebounced.cancel();
      saveTemplateOnServer(
        {
          view: {
            createdFromTemplateId: EMPTY_TEMPLATE_ID,
            templateId: null,
            templateName,
            unsavedView: false,
            current: true,
            currentProjectId: projectId,
            projectId,
            source: projectName,
            dashboardChartState: '',
            experimentQueryState: '',
            isAutoRefreshEnabled: true,
            pinnedExperiments: [],
            reactGridTableState: '',
            v3: {
              isPersonalView: false
            }
          }
        },
        {
          onSuccess: ({ template_id }) => {
            if (isDefault) {
              onSetAsDefaultView({
                viewId: template_id
              });
            }

            changeCurrentView(template_id);

            dispatch(
              alertsUtil.openSnackbarDialog(
                snackbarTypes.SUCCESS_API_RESPONSE,
                `Your view was successfully created`
              )
            );
            onClose();
          },
          onError: e => {
            /// @ts-expect-error fix later
            const { msg } = e.response.data;

            dispatch(
              alertsUtil.openErrorDialog(
                dialogTypes.CATCH_ERROR_SAVE_TEMPLATE,
                `There was an error saving your dashboard view: ${msg}`
              )
            );
          }
        }
      );
    },
    [
      changeCurrentView,
      dispatch,
      onClose,
      onSetAsDefaultView,
      projectId,
      projectName,
      saveTemplateOnServer,
      saveTemplateOnServerDebounced
    ]
  );

  const confirmActionsMap: Record<
    VIEW_SELECT_ACTION_MODE,
    ViewSelectUpsertActionFn
  > = useMemo(
    () => ({
      [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: onEditView,
      [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: onDuplicateView,
      [VIEW_SELECT_ACTION_MODE.NEW_MODE]: onCreateNewView
    }),
    [onCreateNewView, onDuplicateView, onEditView]
  );

  const nameMap: Record<VIEW_SELECT_ACTION_MODE, string> = useMemo(
    () => ({
      [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: `${clonedViewDefaultName} ${
        clonesCount ? `(Copy ${clonesCount})` : '(Copy)'
      }`,
      [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: templateToEdit?.templateName || '',
      [VIEW_SELECT_ACTION_MODE.NEW_MODE]: `${newViewDefaultName}${
        notRenamedNewViewCount ? ` (${notRenamedNewViewCount})` : ''
      }`
    }),
    [
      clonedViewDefaultName,
      clonesCount,
      newViewDefaultName,
      notRenamedNewViewCount,
      templateToEdit?.templateName
    ]
  );

  const formDataMap: Record<
    VIEW_SELECT_ACTION_MODE,
    ViewSelectUpsertFormData
  > = useMemo(
    () => ({
      [VIEW_SELECT_ACTION_MODE.DUPLICATE_MODE]: {
        name: nameMap[mode],
        isDefault: false
      },
      [VIEW_SELECT_ACTION_MODE.EDIT_MODE]: {
        name: nameMap[mode],
        isDefault: Boolean(templateToEdit?.projectDefault)
      },
      [VIEW_SELECT_ACTION_MODE.NEW_MODE]: {
        name: nameMap[mode],
        isDefault: false
      }
    }),
    [mode, nameMap, viewData?.projectDefault]
  );

  const isEditMode = mode === VIEW_SELECT_ACTION_MODE.EDIT_MODE;

  const existedNames = useMemo(() => {
    let names = templatesNames;

    if (isEditMode) {
      names = names.filter(name => name !== viewData?.templateName);
    }
    if (!modalOpened) {
      names = [];
    }

    return names;
  }, [isEditMode, modalOpened, templatesNames, viewData?.templateName]);

  const actionModalConfig: FormViewModalProps = useMemo(
    () => ({
      existedNames,
      formData: formDataMap[mode],
      isEditMode,
      isLoading: isSavingTemplate,
      isOpened: modalOpened,
      onClose,
      onConfirm: confirmActionsMap[mode],
      primaryButtonText: modalCopy.primaryButtonText,
      title: modalCopy.title
    }),
    [
      confirmActionsMap,
      existedNames,
      formDataMap,
      isEditMode,
      isSavingTemplate,
      modalCopy.primaryButtonText,
      modalCopy.title,
      modalOpened,
      mode,
      onClose
    ]
  );

  return {
    actionModalConfig,
    onCreateNewTemplate,
    onDuplicateTemplate,
    onEditTemplate,
    onSelectView,
    onSetAsDefaultView
  };
};

export default useDashboardViewActions;
